import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FundSummary, ResearchEstimate, ResearchStatus } from '@nemo/api';
import {
  STAGE_LABELS,
  dealroomInvestorUrl,
  fundsToCsv,
  stagesInvesting,
  stagesLeading,
} from '@nemo/core/model';
import { useFundAffinityRefresh } from '../affinity/useFundAffinityRefresh.js';
import { countryName, hqCountryNames, investorTypeLabel } from '../dealroom/format.js';
import { download, today } from '../grid/csv.js';
import { regionsTitle } from '../grid/fundColumns.js';
import { affinityOrganizationUrl } from '../portfolio/format.js';
import { idsForLabels, type FundLists } from '../grid/fundWrites.js';
import type { FundWriter } from '../grid/useFundWriter.js';
import type { UndoStack } from '../grid/useUndoStack.js';
import { useTRPC } from '../trpc.js';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { Menu } from '../ui/Menu.js';
import { useToast } from '../ui/Toast.js';
import { useFundEdits } from './edits.js';
import { SiteIcon, siteOf } from '../ui/SiteIcon.js';
import { InlineText, PickField, hostOf } from './fields.js';
import { PaneLinks, type OutsideLink } from './PaneLinks.js';
import { PaneActivity } from './PaneActivity.js';
import { PaneDetails } from './PaneDetails.js';
import { PanePeople } from './PanePeople.js';
import { PaneNotes, PaneProfile } from './PaneProfile.js';
import { DockedPane, PaneBack, PaneClose, usePaneSheet } from './DockedPane.js';
import { useAssistantFocus } from '../assistant/focus.js';
import { ONLY_FORMER_TEAM } from '../grid/fundColumns.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

export interface FundPaneProps {
  fund: FundSummary;
  lists: FundLists;
  /** The latest research job for this fund, if it has ever had one. */
  research: ResearchStatus | undefined;
  estimate: ResearchEstimate | undefined;
  /** Which section the pane was opened at, when it was opened at one. */
  openAt: 'investments' | null;
  /** The person the pane was opened at, from a link naming them on a round. */
  focusPerson: string | null;
  /** How the pane writes a value, so undo from the grid still reaches it. */
  writer: FundWriter;
  undoStack: UndoStack;
  onStartResearch: () => void;
  onClose: () => void;
  /** To the neighbouring visible row, which is what the arrow keys ask for. */
  onStep: (delta: 1 | -1) => void;
}

/**
 * The fund, in the docked pane every record opens in (`DockedPane`). The
 * question it answers first is who we know there and what they have been
 * doing; everything that only identifies the fund is one muted line under
 * its name.
 */
export function FundPane(props: FundPaneProps): React.JSX.Element {
  const { fund, lists } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const sheet = usePaneSheet();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // The assistant is told which fund is open, so "here" means this one.
  useAssistantFocus('fundId', fund.id);
  const edits = useFundEdits(fund, lists, props.writer, props.undoStack);
  const affinity = useFundAffinityRefresh(fund.id);

  const deleteFund = useMutation(
    trpc.funds.delete.mutationOptions({
      onSuccess: () => {
        invalidateFundRows(queryClient, trpc);
        props.onClose();
      },
      onError: (error) => toast(error.message),
    }),
  );

  // Opening a fund is using it: its people who are due a look get one (when
  // the server has that switched on; otherwise this answers that it is off).
  const freshen = useMutation(trpc.people.freshen.mutationOptions());
  const freshenFund = freshen.mutate;
  useEffect(() => {
    freshenFund({ fundId: fund.id });
  }, [fund.id, freshenFund]);

  // The deployment's Affinity workspace, which the portfolio pane links to as well.
  const links = useQuery(trpc.portfolio.links.queryOptions());
  const outside = outsideLinks(fund, links.data?.affinityWebBase ?? null);

  return (
    <DockedPane label={fund.name} onClose={props.onClose} onStep={props.onStep}>
      <header className="pane-head">
        <PaneBack onClose={props.onClose} />

        <div className="pane-head-line">
          <SiteIcon site={siteOf(fund.website)} name={fund.name} size={28} />
          <InlineText
            className="pane-name"
            label="Fund name"
            value={fund.name}
            display={fund.name}
            onCommit={edits.name}
          />

          <span className="pane-head-actions">
            <Menu
              label={`More for ${fund.name}`}
              items={[
                {
                  label: 'Copy link',
                  onSelect: () => {
                    const link = `${window.location.origin}/?fund=${fund.id}`;
                    void navigator.clipboard
                      .writeText(link)
                      .then(() => toast(`Copied the link to ${fund.name}.`, { tone: 'info' }))
                      .catch(() => toast('Could not reach the clipboard.'));
                  },
                },
                {
                  label: 'Export',
                  onSelect: () =>
                    download(`${slugOf(fund.name)}-${today()}.csv`, fundsToCsv([fund])),
                },
                {
                  label: 'Refresh from Affinity',
                  disabled: affinity.busy,
                  onSelect: affinity.start,
                },
                fund.listStatus === 'list'
                  ? { label: 'Stop tracking', onSelect: () => edits.listStatus('pool') }
                  : { label: 'Track fund', onSelect: () => edits.listStatus('list') },
                {
                  label: 'Delete fund',
                  danger: true,
                  onSelect: () => setConfirmingDelete(true),
                },
              ]}
            />
            <PaneClose onClose={props.onClose} />
          </span>
        </div>

        <PaneLinks name={fund.name} links={outside} />

        {affinity.progress !== null && (
          <p className="subtle pane-progress" role="status">
            {affinity.progress}
          </p>
        )}

        {fund.onlyFormerTeam && (
          <p
            className="pane-former-flag"
            title="Nobody on the team who counts has a strong, warm or recent relationship here"
          >
            {ONLY_FORMER_TEAM}:{' '}
            {[
              ...new Set(
                fund.formerTeam.filter((edge) => edge.decent).map((edge) => edge.memberLabel),
              ),
            ].join(', ')}
          </p>
        )}

        <dl className="pane-facts">
          {factsOf(fund).map((fact) => (
            <div className={fact.wide ? 'pane-fact pane-fact-wide' : 'pane-fact'} key={fact.label}>
              <dt className="pane-fact-label">{fact.label}</dt>
              <dd className="pane-fact-value" title={fact.title ?? undefined}>
                {fact.value === '' ? <span className="pane-fact-empty">—</span> : fact.value}
                {fact.observed !== undefined && (
                  <span className="chip-hollow chip-observed" title={fact.observed.title}>
                    {fact.observed.label}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>

        <PickField
          multiple
          className="pane-owners"
          label={`Who at Project A owns ${fund.name}`}
          options={lists.members}
          selectedIds={idsForLabels(fund.owners, lists.members)}
          onCommit={edits.owners}
        >
          <span className="pane-owners-label">Owners</span>
          <span className="pane-owner-chips">
            {fund.owners.length === 0 ? (
              <span className="pane-placeholder">Add owner…</span>
            ) : (
              fund.owners.map((owner) => (
                <span className="owner-chip" key={owner}>
                  {owner}
                </span>
              ))
            )}
          </span>
        </PickField>
      </header>

      <PanePeople
        fundId={fund.id}
        fundName={fund.name}
        members={lists.members}
        sheet={sheet}
        focusPerson={props.focusPerson}
      />
      <PaneActivity fund={fund} focused={props.openAt === 'investments'} />
      <PaneProfile fund={fund} lists={lists} edits={edits} />
      <PaneNotes fund={fund} edits={edits} />
      <PaneDetails
        fund={fund}
        edits={edits}
        research={props.research}
        estimate={props.estimate}
        onStartResearch={props.onStartResearch}
      />

      {confirmingDelete && (
        <ConfirmModal
          title={`Delete ${fund.name}?`}
          body="Everyone on file at it goes too."
          confirmLabel="Delete fund"
          danger
          onConfirm={() => {
            setConfirmingDelete(false);
            deleteFund.mutate({ id: fund.id });
          }}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </DockedPane>
  );
}

/** One labelled cell under the name: what it is called, and what it says. */
interface HeaderFact {
  label: string;
  /** Empty where nobody has filled it in, which the cell shows as a dash. */
  value: string;
  /** The full value, shown on hover when the cell has truncated it. */
  title: string | null;
  /** HQ takes two of the grid's three columns; nothing else needs the room. */
  wide?: boolean;
  /** What the rounds show, where it differs from the value: a hollow chip after it. */
  observed?: { label: string; title: string } | undefined;
}

/**
 * What the fund is, rather than what it has been doing: a three-column grid
 * of labelled cells, Tier/Type/Leads at over HQ/Active in/Invests at, so the columns line
 * up row to row instead of "Leads" wrapping alone onto its own line. A cell
 * truncates rather than wraps, and carries its full value as a title. A fact
 * nobody has filled in keeps its cell and shows a dash, so the cells stay
 * where the eye last found them.
 */
function factsOf(fund: FundSummary): HeaderFact[] {
  const hq = hqCountryNames(fund.hqCountries);
  const hqTitle = fund.hqCountries.length > 0 ? fund.hqCountries.map(countryName).join(', ') : null;
  const tier = fund.tier === null ? '' : String(fund.tier);
  const type = investorTypeLabel(fund.investorType);
  const leadsAt = stagesLeading(fund)
    .map((stage) => STAGE_LABELS[stage])
    .join(', ');
  const investsAt = stagesInvesting(fund)
    .map((stage) => STAGE_LABELS[stage])
    .join(', ');
  return [
    { label: 'Tier', value: tier, title: tier === '' ? null : tier },
    { label: 'Type', value: type, title: type === '' ? null : type },
    { label: 'Leads at', value: leadsAt, title: leadsAt === '' ? null : leadsAt },
    { label: 'HQ', value: hq.text, title: hqTitle, wide: true },
    {
      label: 'Active in',
      value: fund.observedRegions.map((entry) => entry.region).join(', '),
      title: regionsTitle(fund),
      wide: true,
    },
    { label: 'Invests at', value: investsAt, title: investsAt === '' ? null : investsAt },
  ];
}

/**
 * "Website • Dealroom • Affinity": where else this fund lives, each only where
 * there is one to go to.
 */
function outsideLinks(fund: FundSummary, affinityWebBase: string | null): OutsideLink[] {
  const links: OutsideLink[] = [];
  if (fund.website !== null) {
    links.push({
      label: 'Website',
      href: fund.website,
      title: hostOf(fund.website),
      aria: 'on the web',
    });
  }
  const dealroom = dealroomInvestorUrl(fund.dealroomPath);
  if (dealroom !== null) {
    links.push({ label: 'Dealroom', href: dealroom, title: dealroom, aria: 'on Dealroom' });
  }
  const affinity = affinityOrganizationUrl(affinityWebBase, fund.affinityOrganizationId);
  if (affinity !== null) {
    links.push({ label: 'Affinity', href: affinity, title: affinity, aria: 'in Affinity' });
  }
  return links;
}

/** A file a person will look at in a folder wants the fund's name on it. */
function slugOf(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'fund'
  );
}
